import React, { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Modal from '../Modal';
import { notesApi } from '../../requests/api';
import { dropRestOfDoubleClick } from '../../utils/dropRestOfDoubleClick';
import useSpeechInput from './useSpeechInput';

/**
 * A property's or an investor's notes, read like a chat: oldest at the top,
 * the newest right above the box they are written in. Every change is saved
 * as it is made; onChange hears the list after each one.
 */

// One person's notes this close together read as one message.
const GROUP_GAP_MS = 10 * 60 * 1000;
const COMPOSER_MAX_HEIGHT = 132;
const EDITOR_MAX_HEIGHT = 180;
// No lead, or no token: Kore can't take the note however often it is sent.
const NOT_SET_UP = 'KORE_NOT_SET_UP';

export const NotesIcon = ({ size = 13, strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="5" y="3" width="14" height="18" rx="2.5" />
        <path d="M9 8h6M9 12h6M9 16h3.5" />
    </svg>
);

const ExternalIcon = () => (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6.2 1.2h3.6v3.6M9.8 1.2 5 6M8.2 6.8v2.2a.8.8 0 0 1-.8.8H2a.8.8 0 0 1-.8-.8V3.6a.8.8 0 0 1 .8-.8h2.2" />
    </svg>
);

const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
        <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
);

const PencilIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 6h18" />
        <path d="M8 6V4h8v2" />
        <path d="M19 6l-1 14H6L5 6" />
    </svg>
);

const MicIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="9" y="2" width="6" height="12" rx="3" />
        <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
        <path d="M12 18v4" />
    </svg>
);

const SendIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
);

const AlertIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5M12 16h.01" />
    </svg>
);

function dayKey(date) {
    return date.getFullYear() + '-' + date.getMonth() + '-' + date.getDate();
}

function dayLabel(date) {
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);

    if (dayKey(date) === dayKey(today)) return 'Today';
    if (dayKey(date) === dayKey(yesterday)) return 'Yesterday';

    const options = { weekday: 'long', month: 'long', day: 'numeric' };

    if (date.getFullYear() !== today.getFullYear()) options.year = 'numeric';

    return date.toLocaleDateString('en-US', options);
}

function clock(date) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function editedTitle(note) {
    if (!note.edited_at) return null;

    const at = new Date(note.edited_at);
    const when = at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' + clock(at);

    return note.edited_by ? 'Edited by ' + note.edited_by + ', ' + when : 'Edited ' + when;
}

// A request that never reached iCLIMB rejects with fetch's own "Failed to fetch".
function reasonOf(error) {
    return error && error.status ? error.message : 'Couldn’t reach iCLIMB. Check your connection and try again.';
}

function initials(name) {
    return String(name || '')
        .split(/[^A-Za-z0-9]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('');
}

/** Day separators, and runs of one person's notes, oldest first. */
function rowsOf(notes) {
    const rows = [];
    let previous = null;

    notes.forEach((note) => {
        const at = note.created_at ? new Date(note.created_at) : null;
        const previousAt = previous && previous.created_at ? new Date(previous.created_at) : null;
        const newDay = !previous || !at || !previousAt || dayKey(at) !== dayKey(previousAt);

        if (newDay && at) rows.push({ key: 'day-' + note.id, day: dayLabel(at) });

        rows.push({
            key: note.id,
            note,
            at,
            grouped: !newDay && previous.author === note.author && at - previousAt < GROUP_GAP_MS,
        });

        previous = note;
    });

    return rows;
}

/** A textarea that grows with what is typed, up to maxHeight. */
const GrowingTextarea = forwardRef(function GrowingTextarea({ maxHeight, value, ...rest }, ref) {
    const ownRef = useRef(null);
    const element = ref || ownRef;

    useLayoutEffect(() => {
        const node = element.current;

        if (!node) return;

        node.style.height = 'auto';
        node.style.height = Math.min(node.scrollHeight, maxHeight) + 'px';
    }, [value, maxHeight]);

    return <textarea ref={element} rows={1} value={value} {...rest} />;
});

/** The note's text, folded after six lines when it runs longer. */
function NoteText({ text, edited }) {
    const ref = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);

    useLayoutEffect(() => {
        if (ref.current && !expanded) setOverflows(ref.current.scrollHeight > ref.current.clientHeight + 1);
    }, [text, expanded]);

    return (
        <React.Fragment>
            <div ref={ref} className={'NotesWindow-text' + (expanded ? '' : ' NotesWindow-text--folded')}>
                {text}
                {edited && <span className="NotesWindow-edited" title={edited}> (edited)</span>}
            </div>
            {overflows && (
                <button type="button" className="NotesWindow-more" onClick={() => setExpanded(!expanded)}>
                    {expanded ? 'Show less' : 'Show more'}
                </button>
            )}
        </React.Fragment>
    );
}

function NoteRow({ row, isFresh, editing, confirming, deletePrompt, actions }) {
    const { note, at, grouped } = row;
    const isEditing = Boolean(editing && editing.id === note.id);
    const isConfirming = Boolean(confirming && confirming.id === note.id);

    return (
        <div className={'NotesWindow-note' + (grouped ? ' NotesWindow-note--cont' : '') + (isFresh ? ' NotesWindow-note--fresh' : '')}>
            <div className="NotesWindow-gutter">
                {grouped
                    ? at && <span className="NotesWindow-gutterTime" title={clock(at)}>{clock(at).replace(/\s?[AP]M$/, '')}</span>
                    : <div className="NotesWindow-avatar" aria-hidden="true">{initials(note.author)}</div>}
            </div>
            <div className="NotesWindow-body">
                {!grouped && (
                    <div className="NotesWindow-meta">
                        {note.author && <span className="NotesWindow-author">{note.author}</span>}
                        {at && <span className="NotesWindow-time">{clock(at)}</span>}
                    </div>
                )}

                {isEditing ? (
                    <div className="NotesWindow-editArea">
                        <GrowingTextarea
                            className="NotesWindow-editInput"
                            aria-label="Edit note"
                            maxHeight={EDITOR_MAX_HEIGHT}
                            value={editing.text}
                            disabled={editing.saving}
                            autoFocus
                            onChange={(event) => actions.changeEdit(event.target.value)}
                            onKeyDown={actions.editKey}
                        />
                        {editing.error && <div className="NotesWindow-rowError" role="alert">{editing.error}</div>}
                        <div className="NotesWindow-editActions">
                            <span className="NotesWindow-editHint">Esc to cancel · Enter to save</span>
                            <button type="button" className="NotesWindow-ghostBtn" onClick={actions.cancelEdit} disabled={editing.saving}>Cancel</button>
                            <button type="button" className="NotesWindow-primaryBtn" onClick={actions.saveEdit} disabled={editing.saving || !editing.text.trim()}>
                                {editing.saving ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <NoteText text={note.text} edited={editedTitle(note)} />
                )}

                {isConfirming && confirming.error && <div className="NotesWindow-rowError" role="alert">{confirming.error}</div>}
            </div>

            {!isEditing && (
                <div className={'NotesWindow-tools' + (isConfirming ? ' NotesWindow-tools--on' : '')}>
                    {isConfirming ? (
                        <React.Fragment>
                            <span className="NotesWindow-confirmText">{deletePrompt}</span>
                            <button type="button" className="NotesWindow-ghostBtn NotesWindow-ghostBtn--small" onClick={actions.cancelDelete} disabled={confirming.deleting}>Cancel</button>
                            <button type="button" className="NotesWindow-dangerBtn" onClick={actions.confirmDelete} disabled={confirming.deleting}>
                                {confirming.deleting ? 'Deleting…' : 'Delete'}
                            </button>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <button type="button" className="NotesWindow-tool" aria-label="Edit note" title="Edit" onClick={() => actions.startEdit(note)}>
                                <PencilIcon />
                            </button>
                            <button type="button" className="NotesWindow-tool NotesWindow-tool--danger" aria-label="Delete note" title="Delete" onClick={() => actions.askDelete(note)}>
                                <TrashIcon />
                            </button>
                        </React.Fragment>
                    )}
                </div>
            )}
        </div>
    );
}

function Skeleton() {
    return (
        <React.Fragment>
            {[['160px', '92%', '64%'], ['130px', '78%'], ['150px', '86%', '42%']].map((bars) => (
                <div key={bars.join()} className="NotesWindow-skeleton">
                    <div className="NotesWindow-skeletonAvatar" />
                    <div className="NotesWindow-skeletonBars">
                        {bars.map((width, index) => (
                            <div key={width + index} className={'NotesWindow-skeletonBar' + (index === 0 ? ' NotesWindow-skeletonBar--name' : '')} style={{ width }} />
                        ))}
                    </div>
                </div>
            ))}
        </React.Fragment>
    );
}

/**
 * @param {{ type: 'property'|'investor', id: string }} owner whose notes these are
 * @param {Array<{ label: string, href: string, title?: string }>} [links] shown beside the close button
 */
export default function NotesWindow({ owner, title, subtitle, links, theme, deletePrompt, onClose, onChange }) {
    const [notes, setNotes] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [editing, setEditing] = useState(null);
    const [confirming, setConfirming] = useState(null);
    const [freshId, setFreshId] = useState(null);
    const [refreshError, setRefreshError] = useState(null);

    const listRef = useRef(null);
    const composerRef = useRef(null);
    const stickToBottom = useRef(true);
    const mounted = useRef(true);
    // The list as last saved, for answers that land after the window closed.
    const latest = useRef(null);
    // Saves that finish while Kore is being read: its answer predates them.
    const saves = useRef(0);

    const speech = useSpeechInput((phrase) => setDraft((previous) => (previous ? previous + ' ' + phrase : phrase)));

    const publish = (next) => {
        latest.current = next;
        if (mounted.current) setNotes(next);
        if (onChange) onChange(next);
    };

    const atBottom = () => {
        const node = listRef.current;

        return !node || node.scrollHeight - node.scrollTop - node.clientHeight < 4;
    };

    // What was written, changed or deleted in Kore comes in after iCLIMB's copy is already showing.
    const refresh = () => {
        const savesBefore = saves.current;

        setRefreshError(null);

        notesApi
            .refresh(owner)
            .then((list) => (saves.current === savesBefore ? list : notesApi.list(owner)))
            .then((list) => {
                stickToBottom.current = atBottom();
                publish(list);

                if (!mounted.current) return;

                const ids = new Set(list.map((note) => note.id));

                setEditing((current) => (current && !ids.has(current.id) ? null : current));
                setConfirming((current) => (current && !ids.has(current.id) ? null : current));
            })
            .catch((error) => mounted.current && setRefreshError(reasonOf(error)));
    };

    const load = () => {
        setLoadError(null);
        setNotes(null);

        notesApi
            .list(owner)
            .then((list) => {
                stickToBottom.current = true;
                publish(list);

                if (owner.type === 'investor') refresh();
            })
            .catch((error) => mounted.current && setLoadError(reasonOf(error)));
    };

    useEffect(() => {
        load();

        if (composerRef.current) composerRef.current.focus();

        return () => {
            mounted.current = false;
        };
    }, []);

    useLayoutEffect(() => {
        if (stickToBottom.current && notes && listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
            stickToBottom.current = false;
        }
    }, [notes]);

    const close = () => {
        speech.stop();
        onClose();
    };

    // The window goes on the first click; the second must not reach the map under it.
    const closeByClick = () => {
        dropRestOfDoubleClick();
        close();
    };

    useEffect(() => {
        const onKey = (event) => {
            if (event.key !== 'Escape' || editing) return;

            if (confirming) {
                if (!confirming.deleting) setConfirming(null);
                return;
            }

            close();
        };

        window.addEventListener('keydown', onKey);

        return () => window.removeEventListener('keydown', onKey);
    });

    const send = () => {
        const text = draft.trim();

        if (!text || sending || notes === null) return;

        speech.stop();
        setSending(true);
        setSendError(null);

        notesApi
            .add(owner, text)
            .then((note) => {
                saves.current += 1;
                stickToBottom.current = true;
                publish((latest.current || []).concat([note]));

                if (!mounted.current) return;

                setDraft('');
                setSending(false);
                setFreshId(note.id);
                if (composerRef.current) composerRef.current.focus();
            })
            .catch((error) => {
                if (!mounted.current) return;

                setSending(false);
                setSendError({ text: reasonOf(error), retry: error.code !== NOT_SET_UP });
            });
    };

    const actions = {
        startEdit: (note) => {
            setConfirming(null);
            setEditing({ id: note.id, text: note.text, original: note.text, saving: false, error: null });
        },
        changeEdit: (text) => setEditing((current) => ({ ...current, text })),
        cancelEdit: () => setEditing(null),
        saveEdit: () => {
            if (!editing || editing.saving) return;

            const text = editing.text.trim();
            const id = editing.id;

            if (!text) return;

            if (text === editing.original.trim()) {
                setEditing(null);
                return;
            }

            setEditing({ ...editing, saving: true, error: null });

            notesApi
                .update(owner, id, text)
                .then((saved) => {
                    saves.current += 1;
                    publish((latest.current || []).map((note) => (note.id === id ? saved : note)));
                    if (mounted.current) setEditing((current) => (current && current.id === id ? null : current));
                })
                .catch((error) => {
                    if (!mounted.current) return;

                    setEditing((current) => (current && current.id === id ? { ...current, saving: false, error: reasonOf(error) } : current));
                });
        },
        editKey: (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                if (!editing.saving) setEditing(null);
            } else if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                actions.saveEdit();
            }
        },
        askDelete: (note) => {
            setEditing(null);
            setConfirming({ id: note.id, deleting: false, error: null });
        },
        cancelDelete: () => setConfirming(null),
        confirmDelete: () => {
            if (!confirming || confirming.deleting) return;

            const id = confirming.id;

            setConfirming({ ...confirming, deleting: true, error: null });

            notesApi
                .remove(owner, id)
                .then(() => {
                    saves.current += 1;
                    publish((latest.current || []).filter((note) => note.id !== id));
                    if (mounted.current) setConfirming(null);
                })
                .catch((error) => {
                    if (!mounted.current) return;

                    setConfirming((current) => (current && current.id === id ? { ...current, deleting: false, error: reasonOf(error) } : current));
                });
        },
    };

    const composerOff = Boolean(loadError) || notes === null;
    const canSend = !composerOff && !sending && draft.trim() !== '';

    let content;

    if (loadError) {
        content = (
            <div className="NotesWindow-state">
                <div className="NotesWindow-stateIcon NotesWindow-stateIcon--warn"><AlertIcon size={18} /></div>
                <div className="NotesWindow-stateTitle">Couldn’t load the notes</div>
                <div className="NotesWindow-stateText">{loadError}</div>
                <button type="button" className="NotesWindow-retryBtn" onClick={load}>Try again</button>
            </div>
        );
    } else if (notes === null) {
        content = <Skeleton />;
    } else if (notes.length === 0) {
        content = (
            <div className="NotesWindow-state">
                <div className="NotesWindow-stateIcon"><NotesIcon size={18} strokeWidth={1.8} /></div>
                <div className="NotesWindow-stateTitle">No notes yet</div>
                <div className="NotesWindow-stateText">Write the first one below.</div>
            </div>
        );
    } else {
        content = rowsOf(notes).map((row) =>
            row.day ? (
                <div key={row.key} className="NotesWindow-day">
                    <span className="NotesWindow-dayLine" />
                    <span className="NotesWindow-dayLabel">{row.day}</span>
                    <span className="NotesWindow-dayLine" />
                </div>
            ) : (
                <NoteRow
                    key={row.key}
                    row={row}
                    isFresh={row.note.id === freshId}
                    editing={editing}
                    confirming={confirming}
                    deletePrompt={deletePrompt}
                    actions={actions}
                />
            )
        );
    }

    return (
        <Modal show onClose={closeByClick} className={'NotesWindow NotesWindow--' + (theme || 'dark')}>
            <div className="NotesWindow-dialog" role="dialog" aria-modal="true" aria-labelledby="NotesWindow-title">
                <div className="NotesWindow-head">
                    <div className="NotesWindow-headText">
                        <div id="NotesWindow-title" className="NotesWindow-title">{title}</div>
                        {subtitle && <div className="NotesWindow-subtitle">{subtitle}</div>}
                    </div>
                    {links && links.length > 0 && (
                        <React.Fragment>
                            {links.map((link) => (
                                <a key={link.label} className="NotesWindow-link" href={link.href} target="_blank" rel="noopener noreferrer" title={link.title}>
                                    {link.label}
                                    <ExternalIcon />
                                </a>
                            ))}
                            <span className="NotesWindow-divider" />
                        </React.Fragment>
                    )}
                    <button type="button" className="NotesWindow-close" onClick={closeByClick} aria-label="Close">
                        <CloseIcon />
                    </button>
                </div>

                <div className="NotesWindow-list" ref={listRef}>
                    <div className="NotesWindow-listInner">{content}</div>
                </div>

                <div className="NotesWindow-foot">
                    {refreshError && (
                        <div className="NotesWindow-error NotesWindow-error--refresh" role="alert">
                            <AlertIcon />
                            <span className="NotesWindow-errorText">{refreshError}</span>
                            <button type="button" className="NotesWindow-errorAction" onClick={refresh}>Try again</button>
                        </div>
                    )}
                    {sendError && (
                        <div className="NotesWindow-error NotesWindow-error--send" role="alert">
                            <AlertIcon />
                            <span className="NotesWindow-errorText">{sendError.text}</span>
                            {sendError.retry && (
                                <button type="button" className="NotesWindow-errorAction" onClick={send} disabled={sending}>Try again</button>
                            )}
                        </div>
                    )}
                    <div className={'NotesWindow-bar' + (composerOff ? ' NotesWindow-bar--off' : '')}>
                        <GrowingTextarea
                            ref={composerRef}
                            className="NotesWindow-input"
                            aria-label="New note"
                            maxHeight={COMPOSER_MAX_HEIGHT}
                            placeholder={speech.listening ? 'Listening…' : 'Write a note…'}
                            value={draft}
                            disabled={sending || composerOff}
                            onChange={(event) => setDraft(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                                    event.preventDefault();
                                    send();
                                }
                            }}
                        />
                        <button
                            type="button"
                            className={'NotesWindow-mic' + (speech.listening ? ' NotesWindow-mic--on' : '')}
                            onClick={speech.toggle}
                            disabled={sending || composerOff}
                            aria-label={speech.listening ? 'Stop voice input' : 'Voice input'}
                            title={speech.listening ? 'Stop voice input' : 'Voice input'}
                        >
                            <MicIcon />
                        </button>
                        <button
                            type="button"
                            className={'NotesWindow-send' + (canSend ? ' NotesWindow-send--on' : '')}
                            onClick={send}
                            disabled={!canSend}
                            aria-label="Add note"
                            title="Add note"
                        >
                            <SendIcon />
                        </button>
                    </div>
                    <div className="NotesWindow-hint">{draft.trim() ? 'Enter to add · Shift + Enter for a new line' : ''}</div>
                </div>
            </div>
        </Modal>
    );
}
